import React from 'react';
// template components
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import CaratRight from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/CaratRight';
// native components
import FundDetailsForm from '../fundDetailsForm';

export default function FundInformation({ copy, pageProps }) {

  const {
    advisor,
    appendPercentSign, // the 13 IPS funds do not have a percentage sign in the data string, so for those 13 funds, we add it to the vals.
    assetClass,
    firstAvailableLife,
    firstAvailableVar,
    firstAvailableVarSupplementalText,
    fundFactsDate,
    fundInceptionDate,
    fundManagers,
    netAssets,
    objectiveFocus,
    pageTitle,
    pdfLink,
    sectorWeightings,
    sectorWeightingsTitle,
    style,
    subAdvisor,
    topTen,
    topTenTotal,
  } = copy;

  return (
    // NOTE: Elements wrapped in parent fragments will not be rendered by our snapshot tests, so we're using a span.
    <span>
      <SectionContainer
        id={`${pageProps.id}-funds-page-header`}
        backgroundVariant='lightA'
        themeType='lightTheme'>
        <Typography
          component='h1'
          align='center'>
          {pageTitle}
        </Typography>
      </SectionContainer>
      <FundDetailsForm />
      <SectionContainer
        id={`${pageProps.id}-funds-page-overview`}
        backgroundVariant='lightA'
        themeType='lightTheme'>
        <Row align='center'>
          <Col medium={8}>
            <Typography component='h2'>
              Fund Overview
            </Typography>
            {advisor
              && (<>
                <Typography component='h3'>
                  Advisor
                </Typography>
                <Typography>
                  {advisor}
                </Typography>
              </>)}
            {subAdvisor
              && (<>
                <Typography component='h3'>
                  Subadvisor
                </Typography>
                <Typography>
                  {subAdvisor}
                </Typography>
              </>)}
            {fundManagers
              && (<>
                <Typography component='h3'>
                  Fund Managers
                </Typography>
                <Typography>
                  {fundManagers}
                </Typography>
              </>)}
            <Typography component='h3'>
              Objective&#47;Focus:
            </Typography>
            <Typography>
              {objectiveFocus}
            </Typography>
            {assetClass
              && (<>
                <Typography component='h3'>
                  Asset Class:
                </Typography>
                <Typography>
                  {assetClass}
                </Typography>
              </>)}
            {style
              && (
                <>
                  <Typography component='h3'>
                    Style:
                  </Typography>
                  {Object.keys(style).map((key) => {
                    return (
                      <Typography key={key}>
                        {style[key]}
                      </Typography>
                    );
                  })}
                </>
              )}
          </Col>
        </Row>
      </SectionContainer>
      <SectionContainer
        id={`${pageProps.id}-funds-page-fund-facts`}
        backgroundVariant='lightC'
        themeType='lightTheme'>
        <Row align='center'>
          <Col medium={8}>
            <Typography component='h2'>
              Fund Facts as of {fundFactsDate}
            </Typography>
            {fundInceptionDate && (
              <>
                <Typography component='h3'>
                  Fund Inception
                </Typography>
                <Typography>
                  {fundInceptionDate}
                </Typography>
              </>
            )}
            {firstAvailableVar && (
              <>
                <Typography component='h3'>
                  First Available for Variable Annuity
                </Typography>
                <Typography>
                  {firstAvailableVar}
                </Typography>
              </>
            )}

            {firstAvailableVarSupplementalText && (
              <Typography>
                {firstAvailableVarSupplementalText}
              </Typography>
            )}

            {firstAvailableLife && (
              <>
                <Typography component='h3'>
                  First Available for Variable Life
                </Typography>
                <Typography>
                  {firstAvailableLife}
                </Typography>
              </>
            )}

            {netAssets && (
              <>
                <Typography component='h3'>
                  Net Assets
                </Typography>
                <Typography>
                  &#36;{netAssets}
                </Typography>
              </>
            )}
          </Col>
        </Row>
      </SectionContainer>
      {(topTen[0].name !== '') && (
        <SectionContainer
          id={`${pageProps.id}-funds-page-top-ten-portfolio-holdings`}
          backgroundVariant='lightA'
          themeType='lightTheme'>
          <Row align='center'>
            <Col medium={8}>
              <Typography component='h2'>
                Top 10 Portfolio Holdings &#37; of Net Assets
              </Typography>
              {Object.keys(topTen).map((key) => {
                return (
                  <span key={key}>
                    <Typography component='h3'>
                      {topTen[key].name}
                    </Typography>
                    {topTen[key].percent && (
                      <Typography>
                        {topTen[key].percent}{appendPercentSign && (<>&#37;</>)}
                      </Typography>
                    )}
                  </span>
                );
              })}
              <Typography
                component='h3'
                variant='h2'>
                Total &#37; of Net Assets
              </Typography>
              <Typography>
                {topTenTotal}{appendPercentSign && (<>&#37;</>)}
              </Typography>
              {pdfLink
              && (
                <Link
                  linkUrl={pdfLink}
                  variant='buttonLinkTertiary'
                  endAdornment={CaratRight}
                  endAdornmentStylesReset
                  isInternalPdf>
                  Download Publicly Disclosed Holdings
                </Link>
              )}
            </Col>
          </Row>
        </SectionContainer>
      )}
      {(sectorWeightings[0].name !== '') && (
        <SectionContainer
          id={`${pageProps.id}-funds-page-sector-weightings`}
          backgroundVariant='lightC'
          themeType='lightTheme'>
          <Row align='center'>
            <Col medium={8}>
              {sectorWeightings
              && (<>
                <Typography component='h2'>
                  {sectorWeightingsTitle} &#37; of Equities
                </Typography>
                {Object.keys(sectorWeightings).map((key) => {
                  if(sectorWeightings[key].name !== ''){
                    return (
                      <span key={key}>
                        <Typography component='h3'>
                          {sectorWeightings[key].name}
                        </Typography>
                        {sectorWeightings[key].percent && (
                          <Typography>
                            {sectorWeightings[key].percent}{appendPercentSign && (<>&#37;</>)}
                          </Typography>
                        )}
                      </span>
                    );
                  }
                  return false;
                })}
              </>)}
            </Col>
          </Row>
        </SectionContainer>
      )}
    </span>
  );
}
